import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {Routes} from "react-router";
import routes from "../routes";
import ErrorBoundary from "./ErrorBoundary";
import {useBackgroundContext} from "../contexts/BackgroundContext";
import {Route} from "react-router-dom";
import useAuth from "../auth/useAuth";
import {ThemeProvider, Tooltip} from "@mui/material";
import {createCustomTheme} from "../theme";
import useSettings from "../hooks/useSettings";
import DashboardSidebar from "../components/Sidebar/DashboardSidebar";
import usePlatform from "../hooks/usePlatform";
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsMenu from "../components/SettingsMenu";

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme, open }) => ({
    flexGrow: 1,
    boxSizing: 'border-box',
    maxWidth: '100%',
    overflowX: 'hidden',
    transition: theme.transitions.create(['margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),

    // On md+ (where your drawer is persistent), leave space *when open*
    [theme.breakpoints.up('md')]: {
        marginLeft: open ? `${drawerWidth}px` : 0,
        transition: theme.transitions.create(['margin'], {
            easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
            duration: open
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
        }),
    },
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    variants: [
        {
            props: ({ open }) => open,
            style: {
                width: `calc(100% - ${drawerWidth}px)`,
                marginLeft: `${drawerWidth}px`,
                transition: theme.transitions.create(['margin', 'width'], {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            },
        },
    ],
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

export default function AdminLayout() {
    const { settings } = useSettings();
    const [open, setOpen] = React.useState(true);
    const [color] = useBackgroundContext();
    const auth = useAuth();
    const { isVegas } = usePlatform();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);

    const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    const getRoutes = (routes) => {
        return routes.filter((prop, key) => {
            if (prop.collapse) {
                return true;
            }
            return prop.authorities
                ? prop.authorities.every(authority => auth.user?.authorities.includes(authority))
                : true;
        }).map((prop, key) => {
            if (prop.collapse) {
                return getRoutes(prop.views);
            }
            return <Route path={prop.path} key={key} element={prop.component} />;
        });
    };

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const theme = createCustomTheme({
        responsiveFontSizes: settings.responsiveFontSizes,
        theme: settings.theme,
    });

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                <AppBar position="fixed" open={open}>
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            sx={[{ mr: 2 }, open && { display: "none" }]}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Typography variant="h6" noWrap component="div">
                            {isVegas() ? "Vegas Affiliate" : "Winning Partner"}
                        </Typography>

                        {/* pushes settings button to the right */}
                        <Box sx={{ flexGrow: 1 }} />

                        {/* top-right circular settings button */}
                        <Tooltip title="Settings">
                            <IconButton
                                size="large"
                                color="inherit"
                                onClick={handleOpenMenu}
                                aria-controls={openMenu ? "settings-menu" : undefined}
                                aria-haspopup="true"
                                aria-expanded={openMenu ? "true" : undefined}
                                sx={{
                                    borderRadius: "50%", // circular
                                }}
                            >
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>

                        <SettingsMenu
                            anchorEl={anchorEl}
                            open={openMenu}
                            onClose={handleCloseMenu}
                            onSignOut={async () => {
                                handleCloseMenu();
                                await auth.logout();
                            }}
                            email={auth.user?.firstName}
                        />
                    </Toolbar>
                </AppBar>
            <Drawer
                sx={{
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </DrawerHeader>
                <Divider />

                <DashboardSidebar />
            </Drawer>
            <Main open={open}>
                <DrawerHeader />
                <Box sx={{ px: '52px', background: color, paddingY: theme.spacing(2), paddingX: theme.spacing(4) }}>
                    <ErrorBoundary>
                        <Routes>{getRoutes(routes)}</Routes>
                    </ErrorBoundary>
                </Box>
            </Main>
        </Box>
        </ThemeProvider>
    );
}